import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

export const THEME_MODE_STORAGE_KEY = 'cty-theme-mode';
/** Legacy boolean key from the old single dark-mode toggle, kept for migration. */
const LEGACY_DARK_MODE_STORAGE_KEY = 'cty-dark-mode';

function getSystemPrefersDark(): boolean {
    if (globalThis.window === undefined || !globalThis.matchMedia) return false;
    try {
        return globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return false;
    }
}

/** Stored explicit choice wins; legacy toggle migrates; first-time users fall back to the OS colour-scheme preference. */
function getInitialMode(): ThemeMode {
    if (globalThis.window === undefined) return 'light';
    try {
        const stored = globalThis.localStorage.getItem(THEME_MODE_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') return stored;

        const legacy = globalThis.localStorage.getItem(LEGACY_DARK_MODE_STORAGE_KEY);
        if (legacy === 'true') return 'dark';
        if (legacy === 'false') return 'light';
    } catch {
        // storage unavailable (private mode / quota) - ignore
    }
    return getSystemPrefersDark() ? 'dark' : 'light';
}

/** Toggles the `.dark` class on <html>. */
function applyDarkClass(isDark: boolean) {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', isDark);
}

export interface UseDarkModeResult {
    isDark: boolean;
    mode: ThemeMode;
    /** Whether the tenant Dark Mode capability currently allows dark appearance. */
    isDarkModeFeatureEnabled: boolean;
    setMode: (mode: ThemeMode) => void;
    toggle: () => void;
    setIsDark: (value: boolean) => void;
    /** Gate dark appearance without overwriting the user's stored preference. */
    setDarkModeFeatureEnabled: (enabled: boolean) => void;
}

/**
 * App-wide theme (light/dark): persists preference to localStorage `cty-theme-mode`
 * and toggles the `.dark` class on <html>.
 *
 * When the tenant Dark Mode capability is off, the UI is forced to light visually
 * but the stored preference (`mode`) is left unchanged so it can be restored later.
 */
export function useDarkMode(): UseDarkModeResult {
    const [mode, setMode] = useState<ThemeMode>(getInitialMode);
    // Default true until settings confirm otherwise so a stored dark preference
    // is not briefly wiped on first paint.
    const [isDarkModeFeatureEnabled, setDarkModeFeatureEnabled] = useState(true);
    const isDark = isDarkModeFeatureEnabled && mode === 'dark';

    useEffect(() => {
        applyDarkClass(isDark);
    }, [isDark]);

    useEffect(() => {
        try {
            globalThis.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
        } catch {
            // storage unavailable (private mode / quota) - ignore
        }
    }, [mode]);

    const setIsDark = useCallback((value: boolean) => setMode(value ? 'dark' : 'light'), []);
    const toggle = useCallback(() => setMode((prev) => (prev === 'dark' ? 'light' : 'dark')), []);

    return {
        isDark,
        mode,
        isDarkModeFeatureEnabled,
        setMode,
        toggle,
        setIsDark,
        setDarkModeFeatureEnabled,
    };
}
